import { Lock } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  CUSTOM_ROLE_PREFIX,
  INVITABLE_ROLES,
  ROLE_HINTS_RU,
  SYS_ROLE_PREFIX,
  roleSelectGroups,
} from '@/pages/team/teamLogic';
import { useRoles } from '@/pages/company/roles/hooks/useRolesQueries';

/**
 * Унифицированный выбор роли участника (RBAC v2, AK-343 пункт 5).
 *
 * Системная роль кодируется как `sys:<code>`, кастомная — `custom:<uuid>`.
 * Дропдаун показывает две группы (ADR-081 §4): «Системные роли» (замочек) —
 * по `can_invite_codes` управляющего, и «Роли компании» (кастомные) — только
 * тому, кто может их назначать (AK-914). Так одному `<Select>` хватает единого
 * value-пространства, а одинаковые имена не встают рядом.
 */
export type RoleSelection =
  | { kind: 'system'; code: string }
  | { kind: 'custom'; roleId: string };

export function encodeSelection(sel: RoleSelection | null): string | undefined {
  if (!sel) return undefined;
  return sel.kind === 'system' ? `${SYS_ROLE_PREFIX}${sel.code}` : `${CUSTOM_ROLE_PREFIX}${sel.roleId}`;
}

export function decodeSelection(value: string): RoleSelection {
  if (value.startsWith(CUSTOM_ROLE_PREFIX)) {
    return { kind: 'custom', roleId: value.slice(CUSTOM_ROLE_PREFIX.length) };
  }
  return { kind: 'system', code: value.slice(SYS_ROLE_PREFIX.length) };
}

interface TeamRoleSelectProps {
  value: RoleSelection | null;
  onChange: (sel: RoleSelection) => void;
  /** Системные роли, которые можно выдать (`can_invite_codes`). По умолчанию — все. */
  roles?: readonly string[];
  /** Показывать группу «Роли компании» (кастомные). По умолчанию — да. */
  allowCustom?: boolean;
  /** Текущая системная роль участника — видна, даже если выдать её нельзя. */
  currentRole?: string | null;
  placeholder?: string;
  /** Показать 1-строчную подсказку под селектом. */
  showHint?: boolean;
}

export function TeamRoleSelect({
  value,
  onChange,
  roles = INVITABLE_ROLES,
  allowCustom = true,
  currentRole,
  placeholder = 'Выберите роль',
  showHint = true,
}: TeamRoleSelectProps) {
  const rolesQ = useRoles();
  const custom = rolesQ.data?.custom_roles ?? [];
  const groups = roleSelectGroups(roles, custom, allowCustom, currentRole);

  const hint =
    value?.kind === 'system'
      ? ROLE_HINTS_RU[value.code]
      : value?.kind === 'custom'
        ? custom.find((r) => r.id === value.roleId)?.description
        : undefined;

  return (
    <div className="space-y-1.5">
      <Select
        value={encodeSelection(value)}
        onValueChange={(v) => onChange(decodeSelection(v))}
      >
        <SelectTrigger>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {groups.map((g) => (
            <SelectGroup key={g.label}>
              <SelectLabel>{g.label}</SelectLabel>
              {g.items.map((item) => (
                <SelectItem key={item.value} value={item.value} disabled={item.disabled}>
                  {item.value.startsWith(SYS_ROLE_PREFIX) ? (
                    <span className="flex items-center gap-2">
                      {item.label}
                      <Lock className="h-3 w-3 text-muted-foreground" />
                    </span>
                  ) : (
                    item.label
                  )}
                </SelectItem>
              ))}
            </SelectGroup>
          ))}
        </SelectContent>
      </Select>
      {showHint && hint && (
        <p className="text-xs leading-snug text-muted-foreground">{hint}</p>
      )}
    </div>
  );
}
